<script lang="ts">
  // BJL-CHECK: Badge (used, for the report count). The five-dot scale itself is custom —
  // it is a rating readout painted in the vehicle's route color, not a tone chip.
  //
  // Public crowding aggregate: sits inline next to the route badge/heading. Read-only —
  // reporting lives in CrowdingReport.svelte, behind boarding. See crowding.svelte.ts.
  import { Badge } from '@byjackli-labs/ui/Badge'
  import { Colors } from '../../ui/colors'
  import { Type } from '../../ui/theme'
  import { CROWDING_DOT_COUNT, type CrowdingState } from './crowding.svelte'

  interface Props {
    crowding: CrowdingState
    accentColor: string
  }

  const { crowding, accentColor }: Props = $props()

  const dots = $derived(
    Array.from({ length: CROWDING_DOT_COUNT }, (_, i) => {
      const agg = crowding.aggregate
      const filled = agg ? i < agg.dots : false
      const isHalf = !filled && !!agg?.halfDot && i === agg.dots
      return {
        i,
        background: filled
          ? accentColor
          : isHalf
            ? `linear-gradient(90deg, ${accentColor} 50%, ${Colors.borderStrong} 50%)`
            : Colors.borderStrong,
      }
    }),
  )
</script>

<div style="display: flex; align-items: center; gap: 8px; min-height: 14px;">
  {#if crowding.loading}
    <span style="color: {Colors.textMuted}; {Type.caption};">Crowding…</span>
  {:else if crowding.aggregate?.confident}
    <div
      style="display: flex; align-items: center; gap: 3px;"
      aria-label="Crowding {crowding.aggregate.dots} of {CROWDING_DOT_COUNT}"
    >
      {#each dots as dot (dot.i)}
        <span
          style="width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; background: {dot.background};"
        ></span>
      {/each}
    </div>
    <Badge size="xs" variant="soft" register="mono">
      {crowding.aggregate.sampleSize} report{crowding.aggregate.sampleSize === 1 ? '' : 's'}
    </Badge>
  {:else}
    <span style="color: {Colors.textMuted}; {Type.caption};">
      {crowding.justReportedLevel ? 'Thanks — not enough reports yet' : 'Not enough reports yet'}
    </span>
  {/if}
</div>
